import blogSeed from "@/data/blog.generated.json";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import { API } from "@/lib/faro";
import { useSeo, SITE_URL } from "@/lib/seo";
import PictureImg from "@/components/PictureImg";

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });

export default function BlogPage() {
  const [posts, setPosts] = useState(Array.isArray(blogSeed.posts) ? blogSeed.posts : []);
  useSeo({
    title: "Blog de conducción y seguridad vial | Autoescuelas Faro",
    description: "Artículos sobre carnet de conducir, DGT, recuperación de puntos y seguridad vial en Vigo. Actualizado por Autoescuelas Faro.",
    canonical: `${SITE_URL}/blog`,
    ogImage: "/assets/og/blog-autoescuelas-faro-vigo.jpg",
    ogImageAlt: "Blog de Autoescuelas Faro en Vigo",
  });
  useEffect(() => {
    axios.get(`${API}/blog`).then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="blog-page">
      <Header />
      <main className="section-pad bg-[var(--faro-bg)]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Link to="/" data-testid="back-home" className="inline-flex items-center gap-1 text-sm font-bold text-[var(--faro-blue)]">
            <ArrowLeft size={14} /> Volver
          </Link>
          <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter mt-4">Blog Faro</h1>
          <p className="mt-3 text-lg text-[var(--faro-text-soft)] max-w-2xl">
            Noticias, consejos y novedades sobre seguridad vial y formación.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug || p.id}`}
                data-testid={`blog-list-${p.id}`}
                className="group rounded-2xl overflow-hidden border border-[rgba(30,42,143,0.12)] bg-white hover:-translate-y-1 hover:shadow-xl hover:shadow-[rgba(30,42,143,0.15)] transition-all"
              >
                <div className={p.image?.startsWith("/assets/blog/") ? "aspect-[1200/630] overflow-hidden bg-[var(--faro-blue-deep)]" : "aspect-[4/3] overflow-hidden"}>
                  <PictureImg
                    src={p.image}
                    alt={p.title}
                    width={1200}
                    height={630}
                    sizes="(min-width: 1024px) 400px, (min-width: 768px) 50vw, 100vw"
                    className={`w-full h-full ${p.image?.startsWith("/assets/blog/") ? "object-contain" : "object-cover"} group-hover:scale-105 transition-transform duration-500`}
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="uppercase tracking-[0.18em] font-bold text-[var(--faro-red)]">{p.category}</span>
                    <span className="inline-flex items-center gap-1 text-[var(--faro-text-soft)]">
                      <Clock size={12} /> {p.read_minutes} min
                    </span>
                  </div>
                  <h3 className="mt-3 font-display font-bold text-lg leading-snug">{p.title}</h3>
                  <p className="mt-2 text-sm text-[var(--faro-text-soft)]">{p.excerpt}</p>
                  {p.date && (
                    <time dateTime={p.date} data-testid={`blog-date-${p.id}`} className="mt-3 block text-xs text-[var(--faro-text-soft)]">
                      {formatDate(p.date)}
                    </time>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <FloatingWhatsapp />
    </div>
  );
}
